import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IScheduledZoomMeetingDetailsDTO, IScheduledZoomWebinarDetailsDTO, IZoomDAO } from '../../../../../../api/zoom.dao';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { CreateZoomWebinarFormDataVM } from './create-zoom-webinar-form-data.viewmodel';
import { IConnectionDetailsDTO } from '../../../../../../api/connections.dao';
import { DateTimeFormatter } from '../../../../../../infrastructure/implementations/date-formatter';
import { FormattedMessage } from 'react-intl';
import { IAlertsService } from '../../../../../../infrastructure/alerts';
import { ConnectionVersion } from '../../../../../../constants/common.constants';

class CreateZoomWebinarVM {
    @mobx.observable
    private isFormShown: boolean;
    @mobx.observable
    private isSaving: boolean;
    @mobx.observable
    public createZoomWebinarFormDataVM: CreateZoomWebinarFormDataVM;
    @mobx.observable
    private editModeEnabled: boolean = false;
    @mobx.observable
    private webinarDetails: IScheduledZoomWebinarDetailsDTO | null = null;
    @mobx.observable
    private webinarConnectionDetails: IConnectionDetailsDTO;
    @mobx.observable
    private updateWebinarMeetingCallback: (data: IConnectionDetailsDTO) => void;
    @mobx.observable
    private connectionVersion: ConnectionVersion;

    constructor(
        private readonly eventTimeZone: string,
        private readonly zoomWebinarTopic: string,
        private readonly startDateTime: Date,
        private readonly endDateTime: Date,
        public readonly createZoomWebinarCallback: (
            zoomWebinarDetails?: IScheduledZoomWebinarDetailsDTO,
            showAutoRegAndAutoSyncToggles?: boolean
        ) => void,
        private event: { eventId: number; accountId: number },
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly zoomDAO: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        this.isFormShown = false;
        this.isSaving = false;
        this.createZoomWebinarFormDataVM = new CreateZoomWebinarFormDataVM(
            eventTimeZone,
            zoomWebinarTopic,
            startDateTime,
            endDateTime,
        );
    }

    @mobx.action
    public setConnectionVersion(version: ConnectionVersion): void {
        this.connectionVersion = version;
    }

    public getConnectionVersion(): ConnectionVersion {
        return this.connectionVersion;
    }

    @mobx.action
    public setUpdateWebinarMeetingCallback = callback => {
        this.updateWebinarMeetingCallback = callback;
    };

    public async updateWebinarConnectionManually(webinarConnection: IConnectionDetailsDTO = this.webinarConnectionDetails): Promise<void> {
        const connectionDetails = { ...webinarConnection };
        if (this.webinarDetails) {
            try {
                const webinarDetails = await this.zoomDAO.getScheduledWebinarDetails(this.webinarDetails.id);
                connectionDetails.connectionTopic = webinarDetails.topic;
                connectionDetails.zoomDescription = webinarDetails.agenda;
            } catch (error) {
                const errorMessage = this.errorParsingService.extractMessage(error);
                alertServerError('Error', errorMessage);
            }
        }
        if (this.updateWebinarMeetingCallback) this.updateWebinarMeetingCallback(connectionDetails);
        this.hideForm();
        this.disableEditMode();
    }

    @mobx.action
    public enableEditMode(): void {
        this.editModeEnabled = true;
    }
    @mobx.action
    public disableEditMode(): void {
        this.editModeEnabled = false;
    }

    public get isEditMode(): boolean {
        return this.editModeEnabled;
    }

    @mobx.action
    public showForm = () => {
        this.isFormShown = true;
    };

    @mobx.action
    public hideForm = () => {
        this.createZoomWebinarFormDataVM.cleanup();
        this.isFormShown = false;
        this.webinarDetails = null;
    };

    @mobx.action.bound
    public async updateWebinarDetails(): Promise<void> {
        if (!this.createZoomWebinarFormDataVM.checkForm() || this.isSaving) {
            return;
        }

        this.isSaving = true;

        try {
            const { timeZone, startTime, endTime, agenda, topic } = this.createZoomWebinarFormDataVM.getAPIDTO();
            const { registrantsConfirmationEmail, approvalType, autoRecording, ...webinarDetails } = this.webinarDetails;

            const webinarPayload = {
                ...webinarDetails,
                timeZone: timeZone,
                startTime: startTime,
                endTime: endTime,
                agenda: agenda,
                topic: topic,
                settings: {
                    registrantsConfirmationEmail: registrantsConfirmationEmail,
                    approvalType: approvalType,
                    autoRecording: autoRecording
                }
            };

            await this.zoomDAO.updateWebinar({ webinarId: String(webinarPayload.id), ...this.event, ...webinarPayload });
            await this.updateWebinarConnectionManually(this.webinarConnectionDetails);
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError('Error', errorMessage);
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    public createZoomWebinarHandle = async () => {
        let zoomWebinarDetails: IScheduledZoomWebinarDetailsDTO;
        if (!this.createZoomWebinarFormDataVM.checkForm() || this.isSaving) {
            return;
        }

        mobx.runInAction(() => {
            this.isSaving = true;
        });

        try {
            const webinar = this.createZoomWebinarFormDataVM.getAPIDTO();
            const { id: zoomWebinarId } = await this.zoomDAO.scheduleNewWebinar({ ...webinar, ...this.event });
            await this.zoomDAO.updateWebinar({ webinarId: `${zoomWebinarId}`, ...this.event, ...webinar });
            const res = await this.zoomDAO.getScheduledWebinarDetails(zoomWebinarId);
            zoomWebinarDetails = {
                ...res,
                autoRegistrationEnabled: webinar.settings.autoRegistrationEnabled,
                autoSyncEnabled: webinar.settings.autoSyncEnabled,
                approvalType: webinar.settings.approvalType,
                registrantsConfirmationEmail: webinar.settings.registrantsConfirmationEmail,
                autoRecording: webinar.settings.autoRecording,
            };
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError('Error', errorMessage);
            return;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }

        this.createZoomWebinarCallback(zoomWebinarDetails);
        this.hideForm();
    };

    public createZoomWebinarManuallyHandle = async (showAutoRegAndAutoSyncToggles?: boolean) => {
        this.createZoomWebinarCallback(null, showAutoRegAndAutoSyncToggles);
        this.hideForm();
    };

    public getIsFormShown = () => {
        return this.isFormShown;
    };

    public getIsFormSaving = () => {
        return this.isSaving;
    };

    @mobx.action
    private setWebinarDetails(webinarDetails: IScheduledZoomWebinarDetailsDTO): void {
        this.webinarDetails = webinarDetails;
    }

    public get webinarZoomId(): string {
        return this.webinarConnectionDetails?.connectionId ?? '';
    }

    private setWebinarInitialValuesToEdit(details: IScheduledZoomMeetingDetailsDTO): void {
        this.createZoomWebinarFormDataVM.setTimeZone(details.timeZone);
        this.createZoomWebinarFormDataVM.setStartDateTime(DateTimeFormatter.applyTimeZoneToDate(details.startTime, details.timeZone));
        this.createZoomWebinarFormDataVM.setEndDateTime(DateTimeFormatter.applyTimeZoneToDate(details.endTime, details.timeZone));
        this.createZoomWebinarFormDataVM.setMeetingTopic(details.topic);
        this.createZoomWebinarFormDataVM.setDescription(details.agenda);
    }

    public async openEditWebinarModal(webinarConnection: IConnectionDetailsDTO): Promise<void> {
        this.webinarConnectionDetails = webinarConnection;

        this.enableEditMode();

        try {
            const webinarDetails = await this.zoomDAO.getZoomWebinarDetails({
                accountId: this.event.accountId,
                eventId: this.event.eventId,
                webinarId: webinarConnection.connectionId
            });

            this.setWebinarDetails(webinarDetails);
            this.setWebinarInitialValuesToEdit(webinarDetails);
            this.showForm();
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);

            if (error.response.status === 404) {
                this.openWebinarNotFoundModal(errorMessage);
            } else {
                alertServerError('Error', errorMessage);
                this.disableEditMode();
            }
        }
    }

    private openWebinarNotFoundModal(errorMessage: string): void {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    { errorMessage }
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage
                    id = 'oe.common.alerts.editDetailsManually'
                    description = 'Button label'
                    defaultMessage = 'Edit Details Manually'
                />,
                modifier: 'confirm-warn',
                handler: () => {
                    this.updateWebinarConnectionManually();
                },
            },
            cancelButtonProps: {
                text: <FormattedMessage
                    id = 'oe.common.alerts.goBack'
                    description = 'Button label'
                    defaultMessage = 'Go Back'
                />,
                modifier: 'cancel',
                handler: () => {
                    this.hideForm();
                    this.disableEditMode();
                }
            }
        });
    }
}

export { CreateZoomWebinarVM };
